//import search from '../assets/icons/magnifying-glass-solid.svg'
import AssetInventorySummary from '../../utils/AssetInventorySummary'
import clipboard from "../assets/icons/clipboards.png";
import excel from "../assets/icons/excel.png"; 
import acrobat from "../assets/icons/acrobat.png";

import React, {useState, useEffect, useRef} from 'react';
import {saveAs} from 'file-saver';
import FacilityService from "../../services/FacilityService";
import AssetService from "../../services/AssetService";
import ReportService from "../../services/ReportService";
import {If} from "../../utils/Controls";
import {AntdPaging, AntdSelect, AntdDatepicker} from "../../utils/Antd";
import util from "../../utils/util";
import moment from "moment";
import search from "../assets/icons/magnifying-glass-solid.svg"
import downloa from "../assets/icons/download-solid.svg"
import AssetMovementReportUtil from '../../utils/AssetMovementReportUtil'; 
import TransferService from "../../services/TransferService";

import './StatusColour.css'; // Ensure this import is correct
import {
    Button,
    message,
    Tabs,
    Card,
    Row,
    Col,
    Statistic,
    Progress,
    Badge,
    Tooltip,
    Divider
} from 'antd';
import '../assets/css/styles.css';

// Analytics Component for Asset Movement
const AssetMovementAnalytics = ({ data, isNational, assetType }) => {
    const [analytics, setAnalytics] = useState({
        totalTransfers: 0,
        pendingTransfers: 0,
        completedTransfers: 0,
        cancelledTransfers: 0,
        totalAssets: 0,
        facilitiesInvolved: 0,
        averageTransferTime: 0,
        mostActiveMonth: '',
        topTransferringFacility: '',
        topReceivingFacility: '',
        transfersByStatus: {},
        transfersByMonth: {},
        assetsByCategory: {},
        facilityTransferStats: {}
    });

    useEffect(() => {
        if (data && data.length > 0) {
            calculateAnalytics(data);
        }
    }, [data]);

    const calculateAnalytics = (transferData) => {
        const total = transferData.length;
        
        // Status counts
        const statusCounts = transferData.reduce((acc, transfer) => {
            acc[transfer.trstatus] = (acc[transfer.trstatus] || 0) + 1;
            return acc;
        }, {});

        // Unique assets and facilities
        const uniqueAssets = new Set(transferData.map(t => t.asset_name)).size;
        const allFacilities = new Set([
            ...transferData.map(t => t.transfer_from_name),
            ...transferData.map(t => t.transfer_to_name)
        ]);
        const facilitiesCount = allFacilities.size;

        // Monthly transfers
        const monthlyData = transferData.reduce((acc, transfer) => {
            if (transfer.transfer_date) {
                const month = moment(transfer.transfer_date).format('MMM YYYY');
                acc[month] = (acc[month] || 0) + 1;
            }
            return acc;
        }, {});

        // Most active month
        const mostActiveMonth = Object.keys(monthlyData).reduce((a, b) => 
            monthlyData[a] > monthlyData[b] ? a : b, '');

        // Facility transfer statistics
        const facilityStats = transferData.reduce((acc, transfer) => {
            // From facility stats
            if (transfer.transfer_from_name) {
                if (!acc[transfer.transfer_from_name]) {
                    acc[transfer.transfer_from_name] = { sent: 0, received: 0 };
                }
                acc[transfer.transfer_from_name].sent++;
            }
            
            // To facility stats
            if (transfer.transfer_to_name) {
                if (!acc[transfer.transfer_to_name]) {
                    acc[transfer.transfer_to_name] = { sent: 0, received: 0 };
                }
                acc[transfer.transfer_to_name].received++;
            }
            
            return acc;
        }, {});

        // Top transferring and receiving facilities
        const topTransferring = Object.keys(facilityStats).reduce((a, b) => 
            facilityStats[a].sent > facilityStats[b].sent ? a : b, '');
        const topReceiving = Object.keys(facilityStats).reduce((a, b) => 
            facilityStats[a].received > facilityStats[b].received ? a : b, '');

        // Average transfer time (for completed transfers with both dates)
        const completedWithDates = transferData.filter(t => 
            t.transfer_date && t.receive_date && t.trstatus === 'Received'
        );
        
        let avgTransferTime = 0;
        if (completedWithDates.length > 0) {
            const totalDays = completedWithDates.reduce((sum, transfer) => {
                const start = moment(transfer.transfer_date);
                const end = moment(transfer.receive_date);
                return sum + end.diff(start, 'days');
            }, 0);
            avgTransferTime = Math.round(totalDays / completedWithDates.length);
        }

        setAnalytics({
            totalTransfers: total,
            pendingTransfers: statusCounts['Pending'] || statusCounts['In Transit'] || 0,
            completedTransfers: statusCounts['Received'] || statusCounts['Completed'] || 0,
            cancelledTransfers: statusCounts['Cancelled'] || statusCounts['Rejected'] || 0,
            totalAssets: uniqueAssets,
            facilitiesInvolved: facilitiesCount,
            averageTransferTime: avgTransferTime,
            mostActiveMonth: mostActiveMonth,
            topTransferringFacility: topTransferring,
            topReceivingFacility: topReceiving,
            transfersByStatus: statusCounts,
            transfersByMonth: monthlyData,
            facilityTransferStats: facilityStats
        });
    };

    const getStatusColor = (status) => {
        switch (status.toLowerCase()) {
            case 'pending':
            case 'in transit':
                return '#faad14';
            case 'received':
            case 'completed':
                return '#52c41a';
            case 'cancelled':
            case 'rejected':
                return '#ff4d4f';
            default:
                return '#1890ff';
        }
    };

    const getPerformanceLevel = () => {
        if (analytics.averageTransferTime <= 3) return { color: '#52c41a', text: 'Excellent' };
        if (analytics.averageTransferTime <= 7) return { color: '#faad14', text: 'Good' };
        return { color: '#ff4d4f', text: 'Needs Attention' };
    };

    const performanceLevel = getPerformanceLevel();
    const completionRate = analytics.totalTransfers > 0 ? 
        Math.round((analytics.completedTransfers / analytics.totalTransfers) * 100) : 0;

    return (
        <div className="analytics-section mb-4">
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Total Transfers"
                            value={analytics.totalTransfers}
                            valueStyle={{ color: '#1890ff' }}
                        />
                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                            {assetType} Assets
                        </div>
                    </Card>
                </Col>
                
                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Pending Transfers"
                            value={analytics.pendingTransfers}
                            valueStyle={{ color: getStatusColor('pending') }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Completed"
                            value={analytics.completedTransfers}
                            valueStyle={{ color: getStatusColor('completed') }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Unique Assets"
                            value={analytics.totalAssets}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Avg Transfer Time"
                            value={analytics.averageTransferTime}
                            suffix="days"
                            valueStyle={{ color: performanceLevel.color }}
                        />
                        <Badge color={performanceLevel.color} text={performanceLevel.text} />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6} lg={4}>
                    <Card size="small">
                        <Statistic
                            title="Facilities Involved"
                            value={analytics.facilitiesInvolved}
                            valueStyle={{ color: '#13c2c2' }}
                        />
                    </Card>
                </Col>
            </Row>

            {isNational === 1 && (
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                    <Col xs={24} sm={12} md={8}>
                        <Card size="small" title="Transfer Completion Rate">
                            <Progress
                                percent={completionRate}
                                status={completionRate >= 80 ? "success" : completionRate >= 60 ? "normal" : "exception"}
                                strokeColor={completionRate >= 80 ? '#52c41a' : completionRate >= 60 ? '#faad14' : '#ff4d4f'}
                            />
                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                                {analytics.completedTransfers} of {analytics.totalTransfers} completed
                            </div>
                        </Card>
                    </Col>
                    
                    <Col xs={24} sm={12} md={8}>
                        <Card size="small" title="Most Active Period">
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                    {analytics.mostActiveMonth || 'N/A'}
                                </div>
                                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                    Peak Transfer Month
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Card size="small" title="Top Facilities">
                            <div>
                                <Tooltip title="Most transfers sent">
                                    <div style={{ marginBottom: '8px' }}>
                                        <Badge color="#52c41a" text="Top Sender" />
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#595959' }}>
                                            {analytics.topTransferringFacility || 'N/A'}
                                        </div>
                                    </div>
                                </Tooltip>
                                <Tooltip title="Most transfers received">
                                    <div>
                                        <Badge color="#1890ff" text="Top Receiver" />
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#595959' }}>
                                            {analytics.topReceivingFacility || 'N/A'}
                                        </div>
                                    </div>
                                </Tooltip>
                            </div>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Transfer Status Breakdown */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col xs={24}>
                    <Card size="small" title="Transfer Status Breakdown">
                        <Row gutter={[8, 8]}>
                            {Object.entries(analytics.transfersByStatus).map(([status, count]) => (
                                <Col key={status} xs={12} sm={8} md={6} lg={4}>
                                    <div style={{ textAlign: 'center', padding: '8px' }}>
                                        <Badge 
                                            color={getStatusColor(status)} 
                                            text={status}
                                        />
                                        <div style={{ 
                                            fontSize: '18px', 
                                            fontWeight: 'bold', 
                                            color: getStatusColor(status),
                                            marginTop: '4px'
                                        }}>
                                            {count}
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

const Asset1 = () => {
    const [clients, setClients] = useState([]);
    const [items, setItems] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [functionalStatus, setFunctionalStatus] = useState([]);
    const [showAnalytics, setShowAnalytics] = useState(true);
    
    const modules = util.getModules();
    const isNational = util.isNational();
    
    const tabItems = [
        { k: 'Medical', v: 'Med Assets', cat_type: 'Med/NonMed', asset_cat_id: '3' },
        { k: 'Non Medical', v: 'Non Med Assets', cat_type: 'Med/NonMed', asset_cat_id: '2' },
        { k: 'Vehicle', v: 'Vehicle', cat_type: 'Med/NonMed', asset_cat_id: '8' },
        { k: 'Supplies', v: 'Supplies', cat_type: 'Med/NonMed', asset_cat_id: '9' }
    ];
      
    const filteredItems = tabItems.filter(item => {
        if (item.k === 'Medical' && modules.medical === 1) return true;
        if (item.k === 'Non Medical' && modules.non_medical === 1) return true;
        if (item.k === 'Vehicle' && modules.vehicle === 1) return true;
        if (item.k === 'Supplies' && modules.supplies === 1) return true;
        return false;
    });
      
    const [tab, setTab] = useState(filteredItems.length > 0 ? filteredItems[0].k : '');
     
    const onTabChange = (key) => {
        setTab(key);
    }
    
    useEffect(() => {
        FacilityService.all({ status: 1, include_me: 'Y' }).then(res => {
            let allClients = res.data.result.data.map(v => { return { id: v.id, name: v.business_name, is_national: v.is_national, is_me: v.is_me } });
            setClients(allClients);
        });

        AssetService.allItems({ status: 1 }).then(res => {
            let allItems = [];
            allItems = res.data.result.data.map((v) => {
                if (v.cat_type === 'Med/NonMed') {
                    v.item_full_name = v.item_full_name + ' [' + v.cat + ']';
                } else {
                    v.item_full_name = v.item_full_name + ' [' + v.subcat + ']';
                }
                return v;
            });
            setItems(allItems);
        });

        FacilityService.allStationPoints({ status: 1 }).then(res => {
            setStationPoints([...res.data.result.data]);
        });
        
        AssetService.allFunctionalStatus().then(res => {
            setFunctionalStatus([...res.data.data]);
        })
    }, []);

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Assets Movement Report</h2>
                </div>
                <div className="my-auto ml-auto">
                    {isNational === 1 && (
                        <Button 
                            type={showAnalytics ? "primary" : "default"}
                            onClick={() => setShowAnalytics(!showAnalytics)}
                            size="small"
                        >
                            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
                        </Button>
                    )}
                </div>
            </div>

            <div className="page-pad">
                <Tabs
                    activeKey={tab}
                    destroyInactiveTabPane
                    onChange={onTabChange}
                    items={filteredItems.map(rw => ({
                        key: rw.k,
                        label: rw.v,
                        children: (
                            <List
                                items={items}
                                stationPoints={stationPoints}
                                functionalStatus={functionalStatus}
                                cat_type={rw.cat_type}
                                cat={rw.k}
                                asset_cat_id={rw.asset_cat_id}
                                clients={clients}
                                showAnalytics={showAnalytics}
                                assetType={rw.v}
                            />
                        )
                    }))}
                />
            </div>
        </div>
    )
}

function List(props) {
    const [result, setResult] = useState({ data: [], page: {} });
    const [analyticsData, setAnalyticsData] = useState([]);
    const sdataRef = useRef({ p: 1, ps: 25 });
    const [clients, setClients] = useState([]);
    const [items, setItems] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [functionalStatus, setFunctionalStatus] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [summary, setSummary] = useState(null);

    const isNational = util.isNational();
   
    const list = (p, ps, loadAnalytics = false) => {
        sdataRef.current.p = p || 1;
        sdataRef.current.ps = ps || sdataRef.current.ps;
        sdataRef.current.cat_type = props.cat_type;
        sdataRef.current.cat = props.cat;
        sdataRef.current.asset_cat_id = props.asset_cat_id; 

        // For analytics, load all data
        const analyticsParams = loadAnalytics ? { ...sdataRef.current, ps: 0 } : sdataRef.current;
        
        util.showLoader();
        TransferService.transferOuts(analyticsParams).then(({ data }) => {
            const filteredData = data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            
            if (loadAnalytics) {
                setAnalyticsData(filteredData);
                // After loading analytics data, load paginated data for table
                if (filteredData.length > 0) {
                    list(1, 25, false);
                }
            } else {
                setResult({ ...data.result, data: filteredData });
            }
            
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }

    // Load analytics data on component mount for national users
    const loadAnalyticsData = () => {
        if (isNational === 1) {
            list(1, 0, true);
        } else {
            list();
        }
    };

    function getStatusClass(status) {
        switch (status) {
            case 'Borrowed':
                return 'status-open';
            case 'On Hold - Awaiting Spares':
                return 'status-on-hold-awaiting-spares';
            case 'On Hold - Spares Supplied':
                return 'status-on-hold-spares-supplied';
            case 'Completed':
                return 'status-completed';
            case 'Closed':
                return 'status-closed';
            default:
                return '';
        }
    }

    const downloadPdf = () => {
        AssetMovementReportUtil.generateStockTransactionsReport(sdataRef, util.Orientation.LANSCAPE);  
    }
    
    const download = () => {
        sdataRef.current.p = 1;
        sdataRef.current.ps = 0;
        util.showLoader();
        TransferService.transferOuts(sdataRef.current).then(({data}) => {
            let header = {
                asset_name:'ASSET NAME',
                serial_no:'ASSET SERIAL',
                transfer_from_name:'FROM FACILITY',
                transfer_date:'MOVED DATE',
                approved_by_name:'AUTHORIZED BY',
                transfer_to_name:'TO FACILITY',
                trstatus:'TRANSFER STATUS',
                receive_date:'RECEIVED ON',
                reason:'REASON TO MOVE',
                TR38: 'TR38 NUMBER',
            };
    
            let blob = util.convertToCSVBlob(data.result.data, header);
            saveAs(blob, "assets-movement.csv");
    
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }
    
    useEffect(() => {
        setClients(props.clients);
    }, [props.clients]);

    useEffect(() => {
        setItems(props.items.filter(v => v.asset_cat_id === props.asset_cat_id));
    }, [props.items, props.cat_type]);

    useEffect(() => {
        setStationPoints(props.stationPoints);
    }, [props.stationPoints]);

    useEffect(() => {
        setFunctionalStatus(props.functionalStatus);
    }, [props.functionalStatus]);

    useEffect(() => {
        loadAnalyticsData();
        return () => { message.destroy() }
    }, []);

    const handleSearch = () => {
        list();
        // Reload analytics data after search for national users
        if (isNational === 1) {
            setTimeout(() => {
                list(1, 0, true);
            }, 500);
        }
    };

    return (
        <div className="">
            <div className="page-pad">
                {/* Analytics Section - Load by default for national users */}
                {props.showAnalytics && isNational === 1 && (
                    <AssetMovementAnalytics 
                        data={analyticsData} 
                        isNational={isNational}
                        assetType={props.assetType}
                    />
                )}

                {result.data.length > 0 &&
                    <div className="text-secondary my-auto">
                        Showing {result.page.start + 1} - {result.page.start + result.page.total} of {result.page.total_records} records.
                    </div>
                }

                <div className="d-flex tbl-search-head">
                    <div className="my-auto flex-grow-1 pr10">
                        <SearchForm
                            dataRef={sdataRef}
                            onSearch={handleSearch}
                            clients={clients}
                            functionalStatus={functionalStatus}
                            stationPoints={stationPoints}
                            cat_type={props.cat_type}
                            asset_cat_id={props.asset_cat_id}
                            isNational={isNational}
                        />
                    </div>
          
                    <div className="my-auto d-flex" style={{ display: 'flex', alignItems: 'center' }}>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={download} 
                            style={{
                                backgroundColor: 'green', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={excel} alt="download" height={15} /> Export Excel
                        </button>
                        &nbsp;&nbsp;
                        <button 
                            type="button" 
                            onClick={downloadPdf} 
                            style={{
                                backgroundColor: 'red', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '4px', 
                                padding: '10px', 
                                marginRight: '10px'
                            }}
                        >
                            <img src={acrobat} alt="download" height={15} /> Export PDF
                        </button>
                    </div>
                </div>

                <If cond={result.data.length}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm font-xs table-striped table-hover1 m-0">
                            <thead className="thead-light text-uppercase table-text-vmid pad-y-md">
                                <tr>
                                    <th className="w20">SN</th>
                                    {isNational===1 && <th className="w100">FACILITY TRANSFERRED FROM</th>}
                                    <th>ASSET</th>
                                    <th>SERIAL</th>
                                    <th>TRANSFER DATE</th>
                                    <th>AUTHORIZED BY</th>
                                    <th>TRANSFERRED TO</th>
                                    <th>TRANSFER STATUS</th>
                                    <th>RECEIVED/ CANCELLED DATE</th>
                                    <th>REASON FOR TRANSFER</th>
                                    <th>TR38 NUMBER</th>
                                </tr>
                            </thead>
                            <tbody className="table-text-top font-sm">
                                {result.data.map((v, i) => (
                                    <tr key={i}>
                                        <td>{i+1}.</td>
                                        {isNational===1 && <td>{v.transfer_from_name}</td>}  
                                        <td>{v.asset_name}</td>
                                        <td>{v.serial_no}</td>   
                                        <td>{v.transfer_date}</td>   
                                        <td>{v.approved_by_name}</td> 
                                        <td>{v.transfer_to_name}</td>   
                                        <td>
                                            <Badge 
                                                color={
                                                    v.trstatus === 'Pending' || v.trstatus === 'In Transit' ? '#faad14' :
                                                    v.trstatus === 'Received' || v.trstatus === 'Completed' ? '#52c41a' :
                                                    v.trstatus === 'Cancelled' || v.trstatus === 'Rejected' ? '#ff4d4f' : '#1890ff'
                                                }
                                                text={v.trstatus}
                                            />
                                        </td>      
                                        <td>{v.receive_date}</td>  
                                        <td className={`nowrap uc ${getStatusClass(v.reason)}`}>{v.reason}</td>
                                        <td>{v.TR38}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex tbl-foot-bx">
                        <AntdPaging
                            onChange={list}
                            total={result.page.total_records}
                            current={result.page.cur_page}
                            pageSize={sdataRef.current.ps}
                            showSizeChanger
                        />
                    </div>
                </If>
                <If cond={!result.data.length}>
                    <div className="no-rec">No record found</div>
                </If>
            </div>
        </div>
    )
}

const SearchForm = (props) => {
    let { dataRef, onSearch, clients, isNational, cat_type, asset_cat_id } = props;
    let [data, setData] = useState({ ...dataRef.current, station_point_ids: [], isdonated: null });
    let [stationPoints, setStationPoints] = useState([]);
    const [items, setItems] = useState([]);
  
    const handleChange = (v, k) => {
        data[k] = v;
        if (k === 'client_id') {
            data.station_point_ids = [];
        }
        setData({ ...data });
    }
    
    useEffect(() => {
        dataRef.current = { ...data };
    }, [data]);

    useEffect(() => {
        if (isNational === 1) {
            setData({ ...data, client_id: null });
        } else {
            let ob = clients.find(v => v.is_me);
            setData({ ...data, client_id: ob?.id });
        }
    }, [clients]);

    useEffect(() => {
        setStationPoints([...props.stationPoints]);
    }, [props.stationPoints]);

    useEffect(() => {
        setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
    }, [dataRef.current.p, dataRef.current.ps]);

    useEffect(() => {
        AssetService.allItems({ status: 1, cat_type, asset_cat_id, client_id: data.client_id, availableAssetsOnly: 'Y' }).then(res => {
            let allItems = [];
            allItems = res.data.result.data.map((v) => {
                if (v.cat_type === 'Med/NonMed') {
                    v.item_full_name = v.item_full_name + ' [' + v.cat + ']';
                } else {
                    v.item_full_name = v.item_full_name + ' [' + v.subcat + ']';
                }
                return v;
            });
            setItems(allItems);
        });
    }, [cat_type, data.client_id]);

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                {isNational === 1 &&
                    <div className="w150 mr5">
                        <div className="text-secondary fs11 mb2">Facility</div>
                        <AntdSelect
                            placeholder="All Facilities"
                            allowClear
                            showSearch
                            options={clients}
                            value={data.client_id}
                            onChange={v => {
                                data.item_id = null;
                                handleChange(v, 'client_id');
                            }}
                        />
                    </div>
                }
                 
                {cat_type === 'Med/NonMed' &&
                    <div className="w200 mr5">
                        <div className="text-secondary fs11 mb2">Station Point</div>
                        <AntdSelect
                            showSearch
                            allowClear
                            placeholder="Station Point"
                            mode="multiple"
                            options={stationPoints.filter(v => v.client_ids.includes(data.client_id))}
                            value={data.station_point_ids}
                            onChange={v => handleChange(v, 'station_point_ids')}
                        />
                    </div>
                }
                
                <div className="w350 mr5">
                    <div className="text-secondary fs11 mb2">Item</div>
                    <AntdSelect
                        placeholder="Item (All)"
                        allowClear
                        showSearch
                        options={items.map(v => { return { id: v.id, name: v.item_full_name } })}
                        value={data.item_id}
                        onChange={v => handleChange(v, 'item_id')}
                    />
                </div>
                
                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">Start Date</div>
                    <AntdDatepicker
                        placeholder="From Date"
                        allowClear
                        value={data.from_date}
                        onChange={v => {
                            if (data.to_date && moment(v) > moment(data.to_date)) {
                                message.error("From date should be less than or equal to To Date");
                                v = null;
                            }
                            handleChange(v, 'from_date');
                        }}
                    />
                </div>

                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">End Date</div>
                    <AntdDatepicker
                        placeholder="To Date"
                        allowClear
                        value={data.to_date}
                        onChange={v => {
                            if (data.from_date && moment(v) < moment(data.from_date)) {
                                message.error("To date should be greater than or equal to From Date");
                                v = null;
                            }
                            handleChange(v, 'to_date');
                        }}
                    />
                </div>
                
                <div className="pt15">
                    <Button className="mt4" type="primary" onClick={() => onSearch()}>
                        <img src={search} alt='login' className='h-1 mr5' height={15} />
                    </Button>
                </div>
            </div>
        </form>
    )
}

export default Asset1